import { useEffect } from 'react';

/**
 * Injects one or more JSON-LD <script> blocks directly into <head>.
 *
 * Direct-DOM approach (not react-helmet-async) because Helmet's <script> tag
 * handling sometimes gets deduped/merged with pre-existing schemas in
 * index.html, which caused our page-specific schemas to drop out.
 *
 * Each schema becomes a <script type="application/ld+json" data-sd-key="...">
 * tag. On unmount or when the schema prop changes, old tags are removed so
 * stale schemas don't bleed between routes.
 */
let counter = 0;

const StructuredData = ({ schema }) => {
  useEffect(() => {
    if (!schema) return undefined;
    const items = Array.isArray(schema) ? schema : [schema];
    const instanceKey = `sd-${++counter}-${Date.now()}`;
    const added = [];
    items.forEach((obj, i) => {
      if (!obj) return;
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-sd-key', `${instanceKey}-${i}`);
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      added.push(el);
    });
    return () => {
      added.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [schema]);
  return null;
};

// ---------------------------------------------------------------------------
// Reusable schema factories (unchanged from previous export)
// ---------------------------------------------------------------------------

const BRAND = {
  name: 'Bespoke Realty',
  url: 'https://bespokerealty.net',
  logo: 'https://customer-assets.emergentagent.com/job_bespoke-realty/artifacts/dg49n26m_BESPOKE%287%29.png',
  telephone: '+1-901-390-4414',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '7927 Players Forest Drive',
    addressLocality: 'Memphis',
    addressRegion: 'TN',
    postalCode: '38119',
    addressCountry: 'US',
  },
  sameAs: [
    'https://www.facebook.com/bespokerealty',
    'https://www.instagram.com/bespokerealty',
  ],
  areaServed: [
    { '@type': 'State', name: 'Tennessee' },
    { '@type': 'State', name: 'Mississippi' },
    { '@type': 'State', name: 'Arkansas' },
  ],
};

export const schemas = {
  organization: () => ({
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: BRAND.name,
    url: BRAND.url,
    logo: BRAND.logo,
    image: BRAND.logo,
    telephone: BRAND.telephone,
    priceRange: '$$-$$$$',
    description:
      'Black-owned, woman-led boutique real estate brokerage serving Mississippi, Tennessee, and Arkansas. Residential sales, luxury homes, new construction, property management, and association management.',
    address: BRAND.address,
    sameAs: BRAND.sameAs,
    areaServed: BRAND.areaServed,
    knowsAbout: [
      'Residential Real Estate', 'Luxury Homes', 'New Construction',
      'Property Management', 'HOA Management', 'First-Time Home Buyers',
      'Relocation Services', 'Investment Properties',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Real Estate Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Home Buying' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Home Selling' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'New Construction' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Property Management' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'HOA / Association Management' } },
      ],
    },
  }),

  localBusiness: ({ geo, city } = {}) => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BRAND.url}#localbusiness`,
    name: BRAND.name,
    url: BRAND.url,
    logo: BRAND.logo,
    image: BRAND.logo,
    telephone: BRAND.telephone,
    priceRange: '$$-$$$$',
    address: BRAND.address,
    ...(geo && { geo: { '@type': 'GeoCoordinates', latitude: geo.lat, longitude: geo.lng } }),
    ...(city && { areaServed: { '@type': 'City', name: city } }),
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '10:30', closes: '16:30' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday','Sunday'], description: 'By Appointment' },
    ],
    sameAs: BRAND.sameAs,
  }),

  person: (agent) => ({
    '@context': 'https://schema.org',
    '@type': ['Person', 'RealEstateAgent'],
    name: agent.name,
    url: `${BRAND.url}/agents/${agent.slug}`,
    image: agent.photo,
    jobTitle: agent.title || 'REALTOR®',
    description: agent.bio,
    telephone: agent.phone,
    email: agent.email,
    worksFor: {
      '@type': 'RealEstateAgent',
      name: BRAND.name,
      url: BRAND.url,
    },
    knowsAbout: agent.specialties || [],
    areaServed: (agent.service_areas || []).map((a) => ({ '@type': 'City', name: a })),
  }),

  teamList: (agents) => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Bespoke Realty Agents',
    numberOfItems: agents.length,
    itemListElement: agents.map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': ['Person', 'RealEstateAgent'],
        name: a.name,
        image: a.photo,
        jobTitle: a.title || 'REALTOR®',
        url: `${BRAND.url}/agents/${a.slug || a.id}`,
      },
    })),
  }),

  article: (post) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt || post.description,
    image: post.image || post.cover_image,
    author: { '@type': 'Organization', name: post.author || BRAND.name, url: BRAND.url },
    publisher: { '@type': 'Organization', name: BRAND.name, logo: { '@type': 'ImageObject', url: BRAND.logo } },
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${BRAND.url}/blog/${post.slug}` },
  }),

  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url?.startsWith('http') ? it.url : `${BRAND.url}${it.url || ''}`,
    })),
  }),

  cityPlace: (city) => ({
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: `${city.name}, ${city.state}`,
    description: city.description,
    containedInPlace: { '@type': 'State', name: city.stateFull },
    ...(city.geo && { geo: { '@type': 'GeoCoordinates', latitude: city.geo.lat, longitude: city.geo.lng } }),
  }),

  service: ({ name, description, serviceType, areaServed }) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceType || name,
    name,
    description,
    provider: {
      '@type': 'RealEstateAgent',
      name: BRAND.name,
      url: BRAND.url,
    },
    areaServed: (areaServed || ['Tennessee', 'Mississippi', 'Arkansas']).map((a) => ({ '@type': 'State', name: a })),
  }),

  contactPage: () => ({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Bespoke Realty',
    url: `${BRAND.url}/contact`,
    mainEntity: {
      '@type': 'RealEstateAgent',
      name: BRAND.name,
      telephone: BRAND.telephone,
      email: 'office@bespokerealty.net',
      address: BRAND.address,
    },
  }),
};

export default StructuredData;
